/**
 * Crisp live text for the battle scene (owner: combat-builder). Mirrors the UI builder's `uiText`
 * contract (resolution = integer zoom, re-rendered after the web fonts load and on zoom changes) but
 * keeps its bookkeeping inside this scene so every listener is removed on shutdown — the battle
 * scene restarts for each bout, so nothing may accumulate on `scene.events`.
 */
import Phaser from 'phaser';
import { FONT_BODY } from '@/core/config';
import { bus, Events } from '@/core/events';
import { ensureFonts } from '@/ui';

type TextStyle = Phaser.Types.GameObjects.Text.TextStyle;

interface Registry { texts: Set<Phaser.GameObjects.Text>; zoom: number; alive: boolean }
const registries = new WeakMap<Phaser.Scene, Registry>();

export function currentZoom(scene: Phaser.Scene): number {
  return Math.max(1, Math.round(scene.scale?.zoom ?? 1));
}

function registryFor(scene: Phaser.Scene): Registry {
  const existing = registries.get(scene);
  if (existing) return existing;
  const reg: Registry = { texts: new Set(), zoom: currentZoom(scene), alive: true };
  const refresh = () => {
    if (!reg.alive) return;
    const z = currentZoom(scene);
    const changed = z !== reg.zoom;
    reg.zoom = z;
    for (const t of reg.texts) {
      if (!t.active) { reg.texts.delete(t); continue; }
      if (changed) t.setResolution(z); else t.updateText();
    }
  };
  const onSettings = () => refresh();
  const onResize = () => refresh();
  bus.onTyped(Events.SETTINGS_CHANGE, onSettings);
  scene.scale.on(Phaser.Scale.Events.RESIZE, onResize);
  void ensureFonts().then(() => { if (registries.get(scene) === reg) refresh(); }).catch(() => undefined);
  const dispose = () => {
    if (!reg.alive) return;
    reg.alive = false;
    bus.offTyped(Events.SETTINGS_CHANGE, onSettings);
    scene.scale.off(Phaser.Scale.Events.RESIZE, onResize);
    scene.events.off(Phaser.Scenes.Events.SHUTDOWN, dispose);
    scene.events.off(Phaser.Scenes.Events.DESTROY, dispose);
    reg.texts.clear();
    if (registries.get(scene) === reg) registries.delete(scene);
  };
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, dispose);
  scene.events.once(Phaser.Scenes.Events.DESTROY, dispose);
  registries.set(scene, reg);
  return reg;
}

/** Creates a Text with the project body font at integer-zoom resolution; tracked until the scene shuts down. */
export function battleText(scene: Phaser.Scene, x: number, y: number, str: string | string[], style: TextStyle = {}): Phaser.GameObjects.Text {
  const reg = registryFor(scene);
  const t = scene.add.text(Math.round(x), Math.round(y), str, { fontFamily: FONT_BODY, fontSize: '16px', color: '#2a1d14', resolution: reg.zoom, ...style });
  reg.texts.add(t);
  t.once(Phaser.GameObjects.Events.DESTROY, () => reg.texts.delete(t));
  return t;
}
